import { useState } from "react";
import { FilePlus, User, Bot, Sparkles } from "lucide-react";
import { PathwayCard } from "./PathwayCard";
import { ExpertConsultationModal } from "./ExpertConsultationModal";
import { AIChatModal } from "./AIChatModal";

interface ThreePathwayHeroProps {
  onStartNewApplication: () => void;
  isStartingNew: boolean;
  isLoading: boolean;
}

export const ThreePathwayHero = ({ 
  onStartNewApplication, 
  isStartingNew, 
  isLoading 
}: ThreePathwayHeroProps) => {
  const [showExpertModal, setShowExpertModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border border-primary/20 px-8 py-6 md:px-12 md:py-8">
        <div className="relative z-10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium text-primary uppercase tracking-wide">Choose Your Path</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4 leading-tight">
              Your AI PEO Concierge is Ready
            </h1>
            
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Whether you're ready to start your application, need expert guidance, or want to learn more about PEOs, 
              we have the perfect pathway for your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Quick Start Path */}
            <PathwayCard
              icon={FilePlus}
              title="Ready Now? Start the Application"
              description="Ready to begin? Start your PEO application and receive multiple quotes from vetted providers in minutes."
              buttonText={isStartingNew ? "Creating Application..." : "Start Application"}
              onClick={onStartNewApplication}
              disabled={isLoading || isStartingNew}
              loading={isStartingNew}
              badge="Most Popular"
            />

            {/* AI Chat Path */}
            <PathwayCard
              icon={Bot}
              title="Chat with The PEO App's AI"
              description="Get instant answers about PEOs, costs, benefits, and how our platform works from our AI assistant."
              buttonText="Start AI Chat"
              onClick={() => setShowAIModal(true)}
            />

            {/* Expert Consultation Path */}
            <PathwayCard
              icon={User}
              title="Meet with our Certified PEO Experts"
              description="Need to talk first? We're real PEO experts with years of experience. We're here to help. Let's chat!"
              buttonText="Schedule Consultation"
              onClick={() => setShowExpertModal(true)}
            />
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>Free to use - no hidden fees</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>Get quotes from multiple PEO providers</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>Expert guidance available</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-24 h-24 bg-primary/10 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-primary/3 rounded-full blur-3xl"></div>
      </div>

      {/* Modals */}
      <ExpertConsultationModal 
        open={showExpertModal} 
        onOpenChange={setShowExpertModal} 
      />
      <AIChatModal 
        open={showAIModal} 
        onOpenChange={setShowAIModal} 
      />
    </>
  );
};